import React, { useState, useEffect } from 'react';
import { Modal, Input, Table, Spin, Button, Space, Tag, Flex } from 'antd';
import { SearchOutlined, UserOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useDebounce } from 'hooks/useDebounce';
import { formatDate } from '../../../utils/format-date';
import styles from './global-search-modal.module.scss';
import { _getUserLeadAssignments } from 'controllers/lead-assignments/lead-assignments-controller';
import { formatGrade, formatPhoneNumber } from 'util/schoolUtils';

const { Search } = Input;

export const GlobalSearchModal = ({ open, onClose, onSelectLead, userId, loading = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const debouncedSearch = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 3) {
      performSearch(debouncedSearch, 1, pagination.pageSize);
    } else {
      setSearchResults([]);
      setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
    }
  }, [debouncedSearch]);

  const searchFunction = async (searchTerm, page = 1, pageSize = 10) => {
    try {
      const response = await _getUserLeadAssignments({
        school_id: 0,
        textSearch: searchTerm,
        page,
        pageSize,
      });
      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error('Global search error:', error);
      openNotification({
        message: 'Search Error',
        description: 'An error occurred while searching. Please try again.',
        type: 'error',
      });
      return [];
    }
  };

  const performSearch = async (term, page = 1, pageSize = 10) => {
    setIsSearching(true);
    try {
      const results = await searchFunction(term, page, pageSize);
      console.log('Global search results:', results);
      setSearchResults(results.result || results || []);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: results.totalCount || results.length || 0,
      }));
    } catch (error) {
      console.error('Global search error:', error);
      setSearchResults([]);
      setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
    } finally {
      setIsSearching(false);
    }
  };

  const handleTableChange = (paginationInfo) => {
    if (searchTerm && searchTerm.length >= 3) {
      performSearch(searchTerm, paginationInfo.current, paginationInfo.pageSize);
    }
  };

  const handleSelectLead = (lead) => {
    onSelectLead?.(lead);
    onClose();
  };

  const handleModalClose = () => {
    setSearchTerm('');
    setSearchResults([]);
    setPagination({ current: 1, pageSize: 10, total: 0 });
    onClose();
  };

  // Ant Design no tiene íconos específicos de "Padre" o "Estudiante", pero puedes usar UserOutlined para ambos y diferenciarlos por color o tooltip.
  // Alternativamente, puedes usar IdcardOutlined para el estudiante y UserOutlined para el padre para diferenciarlos visualmente.

  const columns = [
    {
      title: 'Lead Info',
      key: 'leadInfo',
      render: (_, record) => (
        <div className={styles.leadInfo}>
          <div className={styles.parentName}>
            <UserOutlined style={{ color: '#1890ff', marginRight: 4 }} title="Parent" />
            {record.parent_first_name} {record.parent_last_name}
          </div>
          {record.child_first_name && record.child_last_name && (
            <div className={styles.childName}>
              <UserOutlined style={{ color: '#52c41a', marginRight: 4 }} title="Student" />
              {record.child_first_name} {record.child_last_name}
            </div>
          )}
          ID: {record.id}
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'ContactInfo',
      render: (_, record) => (
        <div className={styles.contactInfo}>
          {record.email && (
            <span className={styles.email}>
              <MailOutlined className={styles.icon} />
              {record.email}
            </span>
          )}
          {record.phone && (
            <span className={styles.phone}>
              <PhoneOutlined className={styles.icon} />
              {formatPhoneNumber(record.phone)}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Grade',
      dataIndex: 'grade',
      key: 'grade',
      render: (grade) => formatGrade(grade),
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'School',
      dataIndex: 'school_name',
      key: 'school',
      render: (text, record) => (
        <span className={styles.schoolName}>
          {text}
          <br />
          {record.user_id_assigned ? (
            record.user_id_assigned === userId ? (
              <UserOutlined style={{ color: '#1890ff', fontSize: '14px' }} title="Assigned to me" />
            ) : (
              <>
                <UserOutlined style={{ color: '#8c8c8c', fontSize: '12px' }} />
                <i style={{ color: '#8c8c8c', fontSize: '12px' }}>{` ${record.email_assigned}`}</i>
              </>
            )
          ) : (
            ''
          )}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} className={styles.statusTag}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          href={`/adminv2/schools/${record.school_id}/relationship-manager?leadid=${record.id}&status=${record.status}`}
          target="_blank"
          rel="noopener noreferrer">
          View on RM
        </Button>
      ),
    },
  ];

  const getStatusColor = (status) => {
    const statusColors = {
      New: 'blue',
      Contacted: 'orange',
      Qualified: 'green',
      'Application Sent': 'purple',
      Enrolled: 'success',
      'Not Interested': 'red',
      Waitlisted: 'gold',
    };
    return statusColors[status] || 'default';
  };

  return (
    <Modal
      title={
        <Flex align="center" gap="small">
          <SearchOutlined />
          Global Lead Search
        </Flex>
      }
      open={open}
      onCancel={handleModalClose}
      footer={null}
      width={1000}
      className={styles.globalSearchModal}>
      <div className={styles.searchContainer}>
        <Search
          placeholder="Search by parent name, email, phone, or student name (minimum 3 characters)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="large"
          allowClear
          className={styles.searchInput}
        />

        {searchTerm && searchTerm.length < 3 && (
          <div className={styles.searchHint}>Please enter at least 3 characters to search</div>
        )}
      </div>

      <div className={styles.resultsContainer}>
        {isSearching ? (
          <div className={styles.loadingContainer}>
            <Spin size="large" />
            <div>Searching leads...</div>
          </div>
        ) : searchResults.length > 0 ? (
          <>
            <div className={styles.resultsHeader}>
              Found {pagination.total} lead{pagination.total !== 1 ? 's' : ''}
            </div>
            <Table
              dataSource={searchResults}
              columns={columns}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} leads`,
              }}
              size="middle"
              className={styles.resultsTable}
              onChange={handleTableChange}
            />
          </>
        ) : searchTerm && searchTerm.length >= 3 && !isSearching ? (
          <div className={styles.noResults}>
            <SearchOutlined className={styles.noResultsIcon} />
            <div>No leads found matching your search criteria</div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};
